import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PURPOSE_OPTIONS } from "../data/inquirySteps";

const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const ROW_MS = 700;
const FINAL = "Matching games, hosts and production";

const fmtDate = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };

export const curatingRows = (form) => {
  const other = form.event_type === "Other";
  const opt = (PURPOSE_OPTIONS[form.event_type] || []).find((o) => o.value === form.purpose);
  const purposeLabel = form.event_type === "Private Celebration" ? "Occasion" : "Purpose";
  const purpose = other || !opt || opt.text ? "Custom" : opt.all ? "Networking, Team Building, Leadership Development, a Memorable Experience" : opt.value;
  const rows = [`Event type: ${other ? "Custom event" : form.event_type}`];
  if (form.guest_range) rows.push(`Guests: ${form.guest_range}`);
  rows.push(`${purposeLabel}: ${purpose}`);
  if (form.preferred_date) rows.push(`Date: ${fmtDate(form.preferred_date)}${form.date_flexible ? " (flexible)" : ""}`);
  else if (form.date_flexible) rows.push("Date: Flexible");
  if (form.location?.trim()) rows.push(`Location: ${form.location.trim()}`);
  rows.push(FINAL);
  return rows;
};

const Tick = ({ still, size = 22, delay = 0 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className="shrink-0" aria-hidden>
    <motion.circle cx="12" cy="12" r="10" fill="none" stroke="#149B9E" strokeWidth="2" initial={{ pathLength: still ? 1 : 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.35, delay, ease: "easeOut" }} />
    <motion.path d="M7 12.5 L10.5 16 L17 8.5" fill="none" stroke="#149B9E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: still ? 1 : 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.3, delay: delay + (still ? 0 : 0.25), ease: "easeOut" }} />
  </svg>
);

export const CuratingInterstitial = ({ rows, onDone }) => {
  const [shown, setShown] = useState(0);
  const [done, setDone] = useState(false);
  const btn = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const still = reduced();
  useEffect(() => {
    const timers = rows.map((_, i) => setTimeout(() => setShown(i + 1), 350 + i * ROW_MS));
    timers.push(setTimeout(() => {
      setDone(true);
      if (!reduced()) confetti({ particleCount: 70, spread: 75, startVelocity: 30, origin: { y: 0.6 }, colors: ["#149B9E", "#FFC20E", "#ffffff"], scalar: 0.9 });
      setTimeout(() => btn.current?.focus(), 80);
    }, 350 + rows.length * ROW_MS + 450));
    timers.push(setTimeout(() => onDoneRef.current?.(), 350 + rows.length * ROW_MS + 450 + 700 + 2000)); // check draw (~0.7s) + 2s
    return () => timers.forEach(clearTimeout);
  }, [rows]);
  return (
    <div data-testid="curating-interstitial" className="px-1 py-2 sm:py-4" aria-live="polite" aria-busy={!done}>
      <h3 className="font-display text-3xl leading-tight sm:text-4xl">Curating your event</h3>
      <ul data-testid="curating-rows" className="mt-6 space-y-3">
        {rows.slice(0, shown).map((r, i) => (
          <motion.li key={r} data-testid={`curating-row-${i}`} initial={still ? false : { opacity: 0, x: -36 }} animate={{ opacity: 1, x: 0 }} transition={{ type: "spring", stiffness: 420, damping: 22, mass: 0.8 }} className={`flex items-center gap-3 text-base sm:text-lg ${i === rows.length - 1 ? "font-medium text-black" : "text-black/80"}`}>
            <Tick still={still} />{r}
          </motion.li>
        ))}
      </ul>
      {done && (
        <motion.div data-testid="curating-done" initial={still ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 flex flex-col items-center text-center">
          <Tick still={still} size={72} />
          <p className="font-display mt-4 text-2xl text-black">Your event plan is ready!</p>
          <button type="button" ref={btn} data-testid="curating-continue" onClick={onDone} className="btn-primary mt-6 !px-8 !py-4">Continue to contact info <ArrowRight className="h-4 w-4" /></button>
        </motion.div>
      )}
    </div>
  );
};
